/**
 * Client-side validation for the sign-in / create-account form.
 *
 * The form is filled both by `ionInput` events and by the browser password
 * manager, and an autofilled field fires no event: the text lands in the DOM
 * while the React state stays empty. `LoginPage` therefore reads the live
 * `IonInput` values through refs and passes them here, so validation sees what
 * the user actually sees on screen.
 *
 * The rules mirror the API (`loginSchema` / `registerSchema` in
 * `@ai-media/shared` plus the server-side strength policy in
 * `apps/api/src/auth/passwords.ts`) so a form the server would accept is never
 * rejected client-side, and an invalid one fails fast with a specific message
 * instead of a blanket "Required".
 */

export type AuthMode = 'signin' | 'signup';

export interface AuthFormValues {
  name: string;
  email: string;
  password: string;
}

export interface AuthFormError {
  field: keyof AuthFormValues;
  messageKey: string;
  /** Interpolation values for keys such as `validation.minLength`. */
  params?: Record<string, string | number>;
}

/** Matches `registerSchema`'s minimum; `loginSchema` deliberately allows any length. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Deliberately permissive: the API owns authoritative email validation, and
 * this only rejects input that could not possibly be an address, so the user is
 * not blocked by a stricter client rule than the server enforces.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Returns the first problem found, or `null` when the form can be submitted. */
export function validateAuthForm(mode: AuthMode, values: AuthFormValues): AuthFormError | null {
  const email = values.email.trim();

  if (!email) return { field: 'email', messageKey: 'auth.emailRequired' };
  if (!EMAIL_PATTERN.test(email)) return { field: 'email', messageKey: 'validation.invalidEmail' };

  // Password managers frequently supply a password with no trailing space, but
  // a stray space from a paste would otherwise look like a wrong credential.
  const password = values.password;

  if (!password) return { field: 'password', messageKey: 'auth.passwordRequired' };

  // Sign-in only requires that a credential was supplied; the server owns the
  // real policy, so an account with a shorter legacy password can still sign in.
  if (mode === 'signup') {
    if (password.length < MIN_PASSWORD_LENGTH) {
      return {
        field: 'password',
        messageKey: 'validation.minLength',
        params: { min: MIN_PASSWORD_LENGTH },
      };
    }
    // Matches `passwordIssues` on the API: one letter and one digit.
    if (!/[a-z]/i.test(password) || !/\d/.test(password)) {
      return { field: 'password', messageKey: 'auth.passwordStrength' };
    }
  }

  return null;
}